import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/browser";
import Root from "components/Root";
import ErrorPage from "components/Pages/ErrorPage";
import DevToolbar from "components/Common/DevToolbar";
import { isChromeDesktop, isSelenium, isChromeDesktopLegacy } from "services/browser";

import store from "services/store";
import setSocketConnections from "services/sockets/connections";
import setSocketChannels from "services/sockets/channels";

// Import global styles
import "stylesheets/application.scss";

/**
 * isUnsupportedPlatform checks whether the browser being used is one that
 * supported. Currently we only support a small number of browsers due to the
 * development time needed to support many browsers.
 * @returns {Boolean}
 */
function isUnsupportedPlatform() {
  return !(isChromeDesktop() || isSelenium() || isChromeDesktopLegacy());
}

/**
 * Initialize app
 */
function constructApp() {
  setSocketConnections();
  setSocketChannels();

  // Initialize Sentry
  if (window.__env.SENTRY_DSN) {
    Sentry.init({
      dsn: window.__env.SENTRY_DSN,
      environment: window.__env.SENTRY_ENVIRONMENT,
      beforeSend(event, hint) {
        const error = hint.originalException;

        // we only want to add this extra info for API errors
        if (error && error.config && error.config.url) {
          const domain = `https://${window.location.host}`;

          // We just want the url path as the fingerprint, ignoring any objectIds that would
          // cause the grouping to break
          const urlPath = error.config.url.replace(domain, "").replace(/[0-9a-fA-F]{24}/, "<objectId>");

          // eslint-disable-next-line no-param-reassign
          event.fingerprint = [urlPath, error.status];
          // eslint-disable-next-line no-param-reassign
          event.exception.values[0].type = "UncaughtApiError";
          // eslint-disable-next-line no-param-reassign
          event.exception.values[0].value = `${urlPath} failed with code ${error.status}`;
        }

        return event;
      }
    });
  }

  ReactDOM.render(
    <Provider store={store}>
      <>
        {process.env.NODE_ENV === "development" && (
          <DevToolbar />
        )}
        <Root />
      </>
    </Provider>,
    document.getElementById("app")
  );
}

if (isUnsupportedPlatform()) {
  // Render this here so we don't make request to initialize app
  ReactDOM.render(
    <ErrorPage browserError />,
    document.getElementById("app")
  );
} else {
  constructApp();
}
